import React, { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Stack,
  Chip,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FilterListIcon from "@mui/icons-material/FilterList";
import AddIcon from "@mui/icons-material/Add";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import TablePaginationActions from "../../shared/components/Table/Actions";
import { dateFormat, timeFormat } from "../../util/DateUtil";
import Loading from "../UI/Loading";
import PresencaTableFilter from "./PresencaTableFilter";

/** Builds the "12-24 de 100 registros" label used by both pagination bars. */
function displayedRowsLabel({ from, to, count }) {
  const total = count !== -1 ? `${count} registros` : `mais de ${to}`;
  return `${from}-${to} de ${total}`;
}

/** Formats the "Aula N: HH:mm - HH:mm" schedule text for a presença, if any. */
function formatCronograma(presenca) {
  const aula = presenca.cronograma?.cronogramaAula;
  if (!aula) return "—";
  return `Aula ${aula.ordemAula} · ${timeFormat(aula.horarioInicio)} - ${timeFormat(aula.horarioFim)}`;
}

function PresencaRow({
  presenca,
  index,
  isDeleting,
  onEdit,
  onDelete,
  onDuplicate,
}) {
  return (
    <TableRow hover>
      <TableCell sx={{ maxWidth: 350 }}>{presenca.conteudo}</TableCell>
      <TableCell>{dateFormat(presenca.data)}</TableCell>
      <TableCell>
        {presenca.cronograma ? (
          <Chip
            size="small"
            variant="outlined"
            label={formatCronograma(presenca)}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            —
          </Typography>
        )}
      </TableCell>
      <TableCell align="right">
        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
          <Tooltip title="Editar presença">
            <IconButton
              size="small"
              color="primary"
              onClick={() => onEdit(presenca)}
            >
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {isDeleting ? (
            <Box
              sx={{
                display: "grid",
                placeItems: "center",
                width: 34,
                height: 34,
              }}
            >
              <CircularProgress size={18} color="error" />
            </Box>
          ) : (
            <Tooltip title="Excluir presença">
              <IconButton
                size="small"
                color="error"
                onClick={() => onDelete(presenca.codigo)}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title="Duplicar presença">
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              startIcon={<ContentCopyIcon fontSize="small" />}
              onClick={() => onDuplicate(index)}
            >
              Duplicar
            </Button>
          </Tooltip>
        </Stack>
      </TableCell>
    </TableRow>
  );
}

export default function PresencaTable({
  presencas,
  tdp,
  isLoading,
  isLoadingRemove,
  deletingCodigo,
  handleDelete,
  handleDuplicarPresenca,
  handleChangePage,
  setPresenca,
  showFilters,
  setShowFilters,
  mesSelect,
  setMesSelect,
}) {
  const rows = presencas?.content ?? [];
  const isEmpty = !isLoading && rows.length === 0;

  const paginationProps = useMemo(
    () => ({
      rowsPerPageOptions: [],
      rowsPerPage: presencas?.size || 0,
      component: "div",
      count: presencas?.totalElements ?? 0,
      page: presencas?.number ?? 0,
      onPageChange: handleChangePage,
      labelDisplayedRows: displayedRowsLabel,
      ActionsComponent: TablePaginationActions,
    }),
    [presencas, handleChangePage],
  );

  const handleEdit = useCallback(
    (presenca) => setPresenca(presenca),
    [setPresenca],
  );
  const handleNovaPresenca = useCallback(
    () => setPresenca({ tdp, eNovo: true }),
    [setPresenca, tdp],
  );
  const toggleFilters = useCallback(
    () => setShowFilters((prev) => !prev),
    [setShowFilters],
  );

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ sm: "center" }}
        sx={{ mb: 2 }}
      >
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleNovaPresenca}
        >
          Nova Presença
        </Button>

        <Tooltip title={showFilters ? "Ocultar filtros" : "Filtrar"}>
          <Button
            variant={showFilters ? "contained" : "outlined"}
            onClick={toggleFilters}
            startIcon={<FilterListIcon />}
          >
            Filtros
          </Button>
        </Tooltip>
      </Stack>

      {showFilters && (
        <Box sx={{ mb: 2 }}>
          <PresencaTableFilter
            mesSelect={mesSelect}
            setMesSelect={setMesSelect}
          />
        </Box>
      )}

      {isLoading ? (
        <Loading description="Carregando Presenças..." />
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
          <TablePagination {...paginationProps} />

          <TableContainer>
            <Table size="small" aria-label="Tabela de presenças">
              <TableHead>
                <TableRow
                  sx={{ "& th": { fontWeight: 600, bgcolor: "action.hover" } }}
                >
                  <TableCell sx={{ width: 350 }}>Conteúdo</TableCell>
                  <TableCell>Data</TableCell>
                  <TableCell>Cronograma Aula</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isEmpty ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Stack
                        alignItems="center"
                        spacing={1}
                        sx={{ py: 6, color: "text.secondary" }}
                      >
                        <EventBusyIcon fontSize="large" />
                        <Typography variant="body2">
                          Nenhuma presença encontrada.
                        </Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((presenca, index) => (
                    <PresencaRow
                      key={presenca.codigo}
                      presenca={presenca}
                      index={index}
                      isDeleting={
                        isLoadingRemove && deletingCodigo === presenca.codigo
                      }
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onDuplicate={handleDuplicarPresenca}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {!isEmpty && <TablePagination {...paginationProps} />}
        </Paper>
      )}
    </Box>
  );
}

PresencaTable.propTypes = {
  presencas: PropTypes.shape({
    content: PropTypes.array,
    size: PropTypes.number,
    totalElements: PropTypes.number,
    number: PropTypes.number,
  }).isRequired,
  tdp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isLoading: PropTypes.bool,
  isLoadingRemove: PropTypes.bool,
  deletingCodigo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  handleDelete: PropTypes.func.isRequired,
  handleDuplicarPresenca: PropTypes.func.isRequired,
  handleChangePage: PropTypes.func.isRequired,
  setPresenca: PropTypes.func.isRequired,
  showFilters: PropTypes.bool,
  setShowFilters: PropTypes.func.isRequired,
  mesSelect: PropTypes.any,
  setMesSelect: PropTypes.func.isRequired,
};
